import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../context';

export interface RequireAuthProps {
  children: ReactNode;
  /** Where to send logged-out visitors. Defaults to `/login`. */
  redirectTo?: string;
}

/**
 * Logged-out redirect wrapper. Uses react-router navigation — this was a full-page
 * `window.location.assign` while WS-2 had no router to lean on (see docs/DECISIONS.md).
 * The contract is unchanged: children only render once a user is confirmed present.
 */
export function RequireAuth({
  children,
  redirectTo = '/login',
}: RequireAuthProps): JSX.Element | null {
  const { user, status } = useUser();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center text-fg-3">Checking session…</div>
    );
  }

  if (status === 'unauthenticated' || !user) {
    // `replace` so Back doesn't bounce between the guarded page and /login; `state.from`
    // lets the login page return the member to where they were headed.
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
